import type { BaseQueryFn } from '@reduxjs/toolkit/query/react'
import { createApi } from '@reduxjs/toolkit/query/react'
import type { AxiosRequestConfig } from 'axios'
import message from '@/common/message/message'
import {
  type PagenationRequestType,
  type PaginationRepsonse,
} from '@/connect-types/pagination/pagination.type'
import {
  type LoyaltySchemeTagType,
  type LoyaltySchemeType,
  type RedemptionType,
} from '@/state/entities/loyalty/loyalty.types'
import { buildUrl } from '@/utils/common'

/**
 * The legacy backend has been retired and its URL no longer resolves. Every
 * request fails fast with a clear error instead of hitting the dead host.
 * Delete this slice once the remaining consumers are migrated.
 */
const retiredBackendBaseQuery: BaseQueryFn<{
  url: string
  method: AxiosRequestConfig['method']
  data?: AxiosRequestConfig['data']
  params?: AxiosRequestConfig['params']
}> = async () => ({
  error: {
    status: 410,
    data: 'The legacy backend has been retired',
  },
})

const backendApi = createApi({
  reducerPath: 'backend',
  baseQuery: retiredBackendBaseQuery,

  tagTypes: [
    'Organisation',
    'Organisations',
    'Locations',
    'LocationsStories',
    'Progress',
    'User',
    'Vendors',
    'Location',
    'SIC',
    'UniFiControllers',
    'UniFiLocation',
    'OrganisationSettings',
    'OrganisationUsers',
    'OrganisationUsersVenues',
    'Forms',
    'CustomQuestions',
    'CustomQuestionsEntries',
    'GiftCards',
    'GiftCardsActivations',
    'StripeAccounts',
    'LoyaltySchemes',
    'LoyaltySchemesTags',
    'ReviewPages',
    'ReviewResponses',
    'Segments',
    'ProfileTags',
    'EndUser',
    'Csv',
    'MarketingMenus',
  ],

  endpoints: (build) => ({
    /**
     * LOYALTY START
     */
    getLoyaltySchemes: build.query<
      LoyaltySchemeType[],
      {
        orgId: string
      }
    >({
      query: ({ orgId }) => ({
        url: `/organisations/${orgId}/loyalty/stamp/schemes`,
        method: 'get',
      }),

      providesTags: (items: any) => [
        ...(items ?? []).map((item: any) => ({
          type: 'LoyaltySchemes' as const,
          id: item?.id,
        })),
        { id: 'LIST', type: 'LoyaltySchemes' },
      ],
    }),

    createLoyaltyScheme: build.mutation<
      LoyaltySchemeType,
      {
        orgId: string
        scheme: Partial<LoyaltySchemeType>
      }
    >({
      query: ({ orgId, scheme }) => ({
        url: `/organisations/${orgId}/loyalty/stamp/schemes`,
        method: 'post',
        data: scheme,
      }),

      invalidatesTags: () => [{ id: 'LIST', type: 'LoyaltySchemes' }],
    }),
    updateLoyaltyScheme: build.mutation<
      LoyaltySchemeType,
      {
        orgId: string
        scheme: LoyaltySchemeType
      }
    >({
      query: ({ orgId, scheme: s }) => {
        const scheme = { ...s }
        Object.keys(scheme).forEach(
          (key: any) =>
            scheme[key as keyof LoyaltySchemeType] === null &&
            delete scheme[key as keyof LoyaltySchemeType]
        )

        return {
          url: `/organisations/${orgId}/loyalty/stamp/schemes/${scheme.id}`,
          method: 'put',
          data: scheme,
        }
      },

      transformResponse: (response: LoyaltySchemeType) => {
        message.success('Scheme updated')

        return response
      },
      invalidatesTags: (item: any) => [
        { id: item?.id, type: 'LoyaltySchemes' },
      ],
    }),
    deleteLoyaltyScheme: build.mutation<
      LoyaltySchemeType,
      {
        orgId: string
        scheme: LoyaltySchemeType
      }
    >({
      query: ({ orgId, scheme }) => ({
        url: `/organisations/${orgId}/loyalty/stamp/schemes/${scheme.id}`,
        method: 'delete',
      }),

      invalidatesTags: (item: any) => [
        { id: item?.id, type: 'LoyaltySchemes' },
      ],
    }),

    getLoyaltyScheme: build.query<
      LoyaltySchemeType,
      {
        orgId: string
        schemeId: string
      }
    >({
      query: ({ orgId, schemeId }) => ({
        url: `/organisations/${orgId}/loyalty/stamp/schemes/${schemeId}`,
        method: 'get',
      }),
      providesTags: (item: any) => [{ id: item?.id, type: 'LoyaltySchemes' }],
    }),

    getLoyaltyTags: build.query<
      LoyaltySchemeTagType[],
      {
        orgId: string
        schemeId: string
      }
    >({
      query: ({ orgId, schemeId }) => ({
        method: 'get',
        url: `/organisations/${orgId}/loyalty/stamp/schemes/${schemeId}/secondary-id`,
      }),
      providesTags: (items: any) => [
        ...(items ?? []).map((item: any) => ({
          id: item?.id,
          type: 'LoyaltySchemesTags' as const,
        })),
        { id: 'LIST', type: 'LoyaltySchemesTags' },
      ],
    }),
    createLoyaltyTags: build.mutation<
      LoyaltySchemeTagType,
      {
        orgId: string
        schemeId: string
        tag: Partial<LoyaltySchemeTagType>
      }
    >({
      query: ({ orgId, schemeId, tag }) => ({
        method: 'post',
        url: `/organisations/${orgId}/loyalty/stamp/schemes/${schemeId}/secondary-id`,
        data: tag,
      }),
      invalidatesTags: () => [{ id: 'LIST', type: 'LoyaltySchemesTags' }],
    }),
    updateLoyaltyTags: build.mutation<
      LoyaltySchemeTagType,
      {
        orgId: string
        tag: LoyaltySchemeTagType
      }
    >({
      query: ({ orgId, tag }) => ({
        method: 'put',
        url: `/organisations/${orgId}/loyalty/stamp/schemes/${tag.scheme_id}/secondary-id/${tag.id}`,
        data: tag,
      }),
      invalidatesTags: (item: any) => [
        { id: item?.id, type: 'LoyaltySchemesTags' },
      ],
    }),
    getLoyaltyRedeptionUsers: build.query<
      PaginationRepsonse<RedemptionType>,
      {
        orgId: string
        schemeId: string
        page: PagenationRequestType
      }
    >({
      query: ({ orgId, schemeId, page }) => ({
        method: 'get',
        url: buildUrl(
          `/organisations/${orgId}/loyalty/stamp/schemes/${schemeId}/users/redemptions`,
          page
        ),
      }),
      // invalidatesTags: (item: any) => [{ id: item?.id, type: 'LoyaltySchemesTags' }],
    }),
    /**
     * ----
     * USER QUERIES END
     * ---
     */
  }),
})

export default backendApi
